import React from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import jwtDecode from "jwt-decode"
import { getMyStudentsForReport } from "../../modules/bmd/services/bmd-report.service"
import type { BmdReportStudent } from "../../modules/bmd/types/bmd-report.types"
import { fetchRecommendations } from "../Session/plan-lesson/ai-lesson-planning/recommendationsApi"
import type {
  AiSubject,
  RecommendationApiData,
} from "../Session/plan-lesson/ai-lesson-planning/aiLessonPlanningTypes"
import { buildLearnerReport } from "./learnerReportData"
import {
  SUBJECT_META,
  SUBJECT_ORDER,
  buildSubjectRecs,
  buildSubjectSkills,
  groupActivities,
  updatedStamp,
} from "./learnerReportModel"
import type { SubjectRecs, SubjectSkills } from "./learnerReportModel"
import { initials } from "./studentReportModel"
import { ChevronLeft, RefreshIcon } from "./reportUi"
import type { ReportTab, ReportView } from "./tabs/reportView"
import type { SubjectState } from "./tabs/RecommendationsTab"
import OverviewTab from "./tabs/OverviewTab"
import HistoryTab from "./tabs/HistoryTab"
import SkillsTab from "./tabs/SkillsTab"
import SkillMapTab from "./tabs/SkillMapTab"
import RoadmapTab from "./tabs/RoadmapTab"
import AnalyticsTab from "./tabs/AnalyticsTab"
import RecommendationsTab from "./tabs/RecommendationsTab"
import "./student-report.css"
import "./learner-report.css"

const TABS: Array<{ key: ReportTab; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "history", label: "Learning History" },
  { key: "maths", label: "Maths Skills" },
  { key: "english", label: "English Skills" },
  { key: "map", label: "Skill Map" },
  { key: "roadmap", label: "Roadmap" },
  { key: "analytics", label: "Analytics" },
  { key: "recommendations", label: "AI Recommendations" },
]

function tutorName(): string {
  try {
    const raw = localStorage.getItem("authUser")
    if (!raw) return ""
    const decoded = jwtDecode<{ tut_fname?: string; tut_surname?: string }>(JSON.parse(raw))
    return [decoded?.tut_fname, decoded?.tut_surname].filter(Boolean).join(" ")
  } catch {
    return ""
  }
}

function countryCode(country: string | null | undefined): string | null {
  if (!country) return null
  if (/^(uk|gb|united kingdom|great britain|england)$/i.test(country.trim())) return "uk"
  return country
}

const StudentReportDetailPage: React.FC = () => {
  const { key: studentKey } = useParams<{ key: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get("tab") as ReportTab | null
  const tab: ReportTab = TABS.some(t => t.key === tabParam) ? (tabParam as ReportTab) : "overview"

  const [students, setStudents] = React.useState<BmdReportStudent[]>([])
  const [results, setResults] = React.useState<Partial<Record<AiSubject, SubjectState>>>({})
  const [reloadToken, setReloadToken] = React.useState(0)
  const tutor = React.useMemo(() => tutorName(), [])

  React.useEffect(() => {
    const controller = new AbortController()
    getMyStudentsForReport(controller.signal)
      .then(data => setStudents(data.students))
      .catch(() => {
        if (!controller.signal.aborted) setStudents([])
      })
    return () => controller.abort()
  }, [])

  React.useEffect(() => {
    if (!studentKey) return
    const controller = new AbortController()
    setResults({
      math: { status: "loading", data: null, error: null },
      english: { status: "loading", data: null, error: null },
    })

    Promise.allSettled(
      SUBJECT_ORDER.map(s => fetchRecommendations(studentKey, s, controller.signal))
    ).then(settled => {
      if (controller.signal.aborted) return
      const next: Partial<Record<AiSubject, SubjectState>> = {}
      settled.forEach((r, i) => {
        const subject = SUBJECT_ORDER[i]
        if (r.status === "fulfilled") {
          next[subject] = { status: "success", data: r.value, error: null }
        } else {
          const reason = r.reason as { message?: string } | undefined
          next[subject] = {
            status: "error",
            data: null,
            error: reason?.message || "Failed to load recommendations",
          }
        }
      })
      setResults(next)
    })
    return () => controller.abort()
  }, [studentKey, reloadToken])

  const student = students.find(s => s.pk_student_key === studentKey) || null
  const studentName = student?.name || "Student"
  const firstName = studentName.split(/\s+/)[0] || studentName

  const loading = SUBJECT_ORDER.some(s => !results[s] || results[s]?.status === "loading")
  const loadError = SUBJECT_ORDER.every(s => results[s]?.status === "error")
    ? results.math?.error || "Failed to load recommendations"
    : null

  const report = React.useMemo(() => {
    if (!studentKey || loading || loadError) return null
    const bySubject: Partial<Record<AiSubject, RecommendationApiData | null>> = {}
    SUBJECT_ORDER.forEach(s => {
      bySubject[s] = results[s]?.data ?? null
    })
    return buildLearnerReport(studentKey, studentName, bySubject)
  }, [studentKey, studentName, results, loading, loadError])

  const view = React.useMemo<ReportView | null>(() => {
    if (!report) return null
    const recs = {} as Record<AiSubject, SubjectRecs>
    SUBJECT_ORDER.forEach(s => {
      recs[s] = buildSubjectRecs(s, results[s]?.data ?? null, report)
    })
    const nextTitle = (subject: AiSubject, skill: string) => {
      const k = skill.trim().toLowerCase()
      if (!k) return null
      const hit = [...recs[subject].progress, ...recs[subject].relearn].find(
        p => p.skill.trim().toLowerCase() === k || p.strand.trim().toLowerCase() === k
      )
      return hit ? hit.title : null
    }
    const groups = groupActivities(report.attempts, nextTitle)
    const fallbackYear =
      SUBJECT_ORDER.map(s => results[s]?.data?.learner?.profileYear).find(
        (y): y is number => typeof y === "number"
      ) ?? null
    const skills = {} as Record<AiSubject, SubjectSkills>
    SUBJECT_ORDER.forEach(s => {
      skills[s] = buildSubjectSkills(s, report, groups, recs[s], fallbackYear)
    })
    const enrolled = SUBJECT_ORDER.filter(s => report.student.subjects.includes(s))
    return {
      studentName,
      firstName,
      initialsText: initials(studentName),
      report,
      subjects: enrolled.length ? enrolled : SUBJECT_ORDER,
      recs,
      groups,
      skills,
      country: countryCode(report.student.country),
      now: new Date(),
      nextTitle,
      goTab: (t: ReportTab) => {
        setSearchParams({ tab: t })
        window.scrollTo({ top: 0 })
      },
      planLesson: () => navigate("/session"),
    }
  }, [report, results, studentName, firstName, setSearchParams, navigate])

  const yearValue =
    view?.skills.math.currentYear ?? view?.skills.english.currentYear ?? null
  const headerSubjects = view ? view.subjects : SUBJECT_ORDER
  const updatedLine = SUBJECT_ORDER.filter(s => results[s]?.data)
    .map(s => `${SUBJECT_META[s].label} ${updatedStamp(results[s]?.data?.executedAt)}`)
    .join(" · ")
  return (
    <div className="sr-page">
      <header className="sr-header">
        <div className="sr-header-inner lr-header-inner">
          <div className="sr-row">
            <div className="sr-row-left">
              <Link to="/student-report" className="sr-back sr-no-print">
                <ChevronLeft />
                Students
              </Link>
              <h1 className="sr-title">Learner Report</h1>
            </div>
            <div className="sr-actions sr-no-print">
              {students.length > 1 ? (
                <select
                  className="sr-select"
                  aria-label="Student"
                  value={studentKey || ""}
                  onChange={e => navigate(`/student-report/${e.target.value}?tab=${tab}`)}
                >
                  {student ? null : <option value={studentKey}>{studentName}</option>}
                  {students.map(s => (
                    <option key={s.pk_student_key} value={s.pk_student_key}>
                      {s.name}
                    </option>
                  ))}
                </select>
              ) : null}
              <button
                type="button"
                className="sr-icon-btn lr-refresh"
                aria-label="Refresh data"
                title="Refresh data"
                disabled={loading}
                onClick={() => setReloadToken(t => t + 1)}
              >
                <RefreshIcon />
              </button>
              <button
                type="button"
                className="sr-btn-outline"
                onClick={() => window.print()}
                disabled={!view}
              >
                Download PDF
              </button>
            </div>
          </div>

          <div className="lr-learner">
            <div className="sr-avatar lr-avatar">{initials(studentName)}</div>
            <div className="sr-stack-6">
              <div className="sr-learner-line">
                <span className="lr-learner-name">{studentName}</span>
                {yearValue !== null ? (
                  <span className="sr-subj-chip" style={{ background: "#E8ECF7", color: "#1F2F6B" }}>
                    Year {yearValue}
                  </span>
                ) : student?.enrolment_year_level_name ? (
                  <span className="sr-subj-chip" style={{ background: "#E8ECF7", color: "#1F2F6B" }}>
                    {student.enrolment_year_level_name}
                  </span>
                ) : null}
                {headerSubjects.map(s => (
                  <span
                    key={s}
                    className="sr-subj-chip"
                    style={{ background: SUBJECT_META[s].bg, color: SUBJECT_META[s].fg }}
                  >
                    {SUBJECT_META[s].label}
                  </span>
                ))}
                {report?.student.country ? (
                  <span className="sr-subj-chip" style={{ background: "#EEF0F5", color: "#4A5163" }}>
                    {report.student.country}
                  </span>
                ) : null}
              </div>
              <span className="sr-muted">
                {[tutor ? `Tutor ${tutor}` : "", updatedLine ? `Data updated ${updatedLine}` : ""]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
          </div>

          <nav aria-label="Report sections" className="lr-tabs sr-no-print">
            {TABS.map(t => (
              <Link
                key={t.key}
                to={`?tab=${t.key}`}
                className="lr-tab"
                aria-current={t.key === tab ? "page" : undefined}
              >
                {t.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>

      <main className="sr-main">
        {tab === "recommendations" ? (
          <RecommendationsTab
            results={results}
            firstName={firstName}
            onRetry={() => setReloadToken(t => t + 1)}
          />
        ) : loading ? (
          <div className="sr-card sr-state">Loading learner report…</div>
        ) : loadError || !view ? (
          <div className="sr-card sr-state sr-state-error">
            <div>{loadError || "Failed to load learner report"}</div>
            <button type="button" className="sr-btn-outline" onClick={() => setReloadToken(t => t + 1)}>
              Try again
            </button>
          </div>
        ) : (
          <>
            {view.report.attempts.length === 0 ? (
              <div className="lr-notice" role="note">
                Activity scores and attempt history are not in the recommendations response yet, so
                score-based sections are empty for now.
              </div>
            ) : null}
            {tabBody(view)}
          </>
        )}
      </main>
    </div>
  )

  function tabBody(view: ReportView) {
    switch (tab) {
      case "history":
        return <HistoryTab view={view} />
      case "maths":
        return <SkillsTab view={view} subject="math" />
      case "english":
        return <SkillsTab view={view} subject="english" />
      case "map":
        return <SkillMapTab view={view} />
      case "roadmap":
        return <RoadmapTab view={view} />
      case "analytics":
        return <AnalyticsTab view={view} />
      default:
        return <OverviewTab view={view} />
    }
  }
}

export default StudentReportDetailPage
